import { css } from 'styled-components';

import type { ThemeType } from 'common/types/Styles';

export type SpacingProps = {
  margin?: keyof ThemeType['spacing'];
  marginHorizontal?: keyof ThemeType['spacing'];
  marginVertical?: keyof ThemeType['spacing'];
  marginLeft?: keyof ThemeType['spacing'];
  marginRight?: keyof ThemeType['spacing'];
  marginTop?: keyof ThemeType['spacing'];
  marginBottom?: keyof ThemeType['spacing'];

  padding?: keyof ThemeType['spacing'];
  paddingHorizontal?: keyof ThemeType['spacing'];
  paddingVertical?: keyof ThemeType['spacing'];
  paddingLeft?: keyof ThemeType['spacing'];
  paddingRight?: keyof ThemeType['spacing'];
  paddingTop?: keyof ThemeType['spacing'];
  paddingBottom?: keyof ThemeType['spacing'];

  gap?: keyof ThemeType['spacing'];
};

export type FlexProps = {
  full?: boolean;
  center?: boolean;

  rowReverse?: boolean;
  column?: boolean;
  columnReverse?: boolean;

  wrap?: boolean;
  wrapReverse?: boolean;

  alignCenter?: boolean;
  alignStart?: boolean;
  alignEnd?: boolean;
  alignBaseline?: boolean;
  alignStretch?: boolean;

  contentCenter?: boolean;
  contentStart?: boolean;
  contentEnd?: boolean;
  contentStretch?: boolean;
  contentAround?: boolean;

  justifyCenter?: boolean;
  justifyStart?: boolean;
  justifyEnd?: boolean;
  justifyBetween?: boolean;
  justifyAround?: boolean;
  justifyEvenly?: boolean;
} & SpacingProps;

export const createFlexStyles = (props: FlexProps) => css<FlexProps>`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: flex-start;
  align-content: stretch;

  ${props.full &&
  `
    width: 100%;
    height: 100%;
    flex: 1;
  `}

  ${props.center &&
  `
    align-items: center;
    justify-content: center;
  `}

  ${props.rowReverse && 'flex-direction: row-reverse;'}
  ${props.column && 'flex-direction: column;'}
  ${props.columnReverse && 'flex-direction: column-reverse;'}
  
  ${props.wrap && 'flex-wrap: wrap;'}
  ${props.wrapReverse && 'flex-wrap: wrap-reverse;'}
  
  ${props.alignCenter && 'align-items: center;'}
  ${props.alignStart && 'align-items: flex-start;'}
  ${props.alignEnd && 'align-items: flex-end;'}
  ${props.alignBaseline && 'align-items: baseline;'}
  ${props.alignStretch && 'align-items: stretch;'}
  
  ${props.contentCenter && 'align-content: center;'}
  ${props.contentStart && 'align-content: flex-start;'}
  ${props.contentEnd && 'align-content: flex-end;'}
  ${props.contentStretch && 'align-content: stretch;'}
  ${props.contentAround && 'align-content: space-around;'}
  
  ${props.justifyCenter && 'justify-content: center;'}
  ${props.justifyStart && 'justify-content: flex-start;'}
  ${props.justifyEnd && 'justify-content: flex-end;'}
  ${props.justifyBetween && 'justify-content: space-between;'}
  ${props.justifyAround && 'justify-content: space-around;'}
  ${props.justifyEvenly && 'justify-content: space-evenly;'}

  ${({
    theme,
    margin,
    marginHorizontal,
    marginVertical,
    marginTop,
    marginBottom,
    marginLeft,
    marginRight,
  }) => {
    if (margin) {
      return {
        margin: theme.spacing[margin],
      };
    }

    const styles: Record<string, string> = {};

    if (marginHorizontal) {
      styles.marginLeft = theme.spacing[marginHorizontal];
      styles.marginRight = theme.spacing[marginHorizontal];
    }
    if (marginVertical) {
      styles.marginTop = theme.spacing[marginVertical];
      styles.marginBottom = theme.spacing[marginVertical];
    }

    // Individual sides override horizontal/vertical
    if (marginTop) {
      styles.marginTop = theme.spacing[marginTop];
    }
    if (marginBottom) {
      styles.marginBottom = theme.spacing[marginBottom];
    }
    if (marginLeft) {
      styles.marginLeft = theme.spacing[marginLeft];
    }
    if (marginRight) {
      styles.marginRight = theme.spacing[marginRight];
    }

    return styles;
  }}

  ${({
    theme,
    padding,
    paddingHorizontal,
    paddingVertical,
    paddingTop,
    paddingBottom,
    paddingLeft,
    paddingRight,
  }) => {
    if (padding) {
      return {
        padding: theme.spacing[padding],
      };
    }

    const styles: Record<string, string> = {};

    if (paddingHorizontal) {
      styles.paddingLeft = theme.spacing[paddingHorizontal];
      styles.paddingRight = theme.spacing[paddingHorizontal];
    }
    if (paddingVertical) {
      styles.paddingTop = theme.spacing[paddingVertical];
      styles.paddingBottom = theme.spacing[paddingVertical];
    }

    // Individual sides override horizontal/vertical
    if (paddingTop) {
      styles.paddingTop = theme.spacing[paddingTop];
    }
    if (paddingBottom) {
      styles.paddingBottom = theme.spacing[paddingBottom];
    }
    if (paddingLeft) {
      styles.paddingLeft = theme.spacing[paddingLeft];
    }
    if (paddingRight) {
      styles.paddingRight = theme.spacing[paddingRight];
    }

    return styles;
  }}

  gap: ${({ theme, gap }) => (gap ? theme.spacing[gap] : 0)};
`;
